import { Fragment, useState, useEffect, useRef } from "react";
import { Offcanvas, Spinner, Button, OffcanvasBody } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { getTOSByJobIdAndCandidateIdAndStatus } from "@workspace/job/src/helpers/backend_helper";
import "./ELCForms.scss";
import { changeStatus } from "@workspace/job/src/store/actions";
import PreOfferVerification from "../PreOfferVerification/PreOfferVerification";
import OfferCreation from "../OfferCreation/OfferCreation";
import ELCShareOffer from "../ELCShareOffer/ELCShareOffer";
import TemplatePreviewSideDrawer from "@workspace/job/src/components/JobOverview/TemplatePreviewSideDrawer/TemplatePreviewSideDrawer";
import EditTOS from "../EditTOS/EditTOS";

function ELCForms({
  selectedCandidate,
  isOpen,
  showTOS,
  setElcFormOpen,
  setElcModalOpen,
  setModalForm,
}) {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(1);
  const [tosData, setTosData] = useState(null);
  const [offcanvasWidth, setOffcanvasWidth] = useState("70vw");

  const [isOfferUploaded, setOfferUploaded] = useState(false);
  const [isViewTemplate, setIsViewTemplate] = useState(false);
  const [templatePreviewInfo, setTemplatePreviewInfo] = useState(null);
  const [templatePreviewAction, setTemplatePreviewAction] = useState(null);
  const [loading, setLoading] = useState(false);

  const formikRef = useRef();

  const changeStatusMeta = useSelector(
    (state) => state.PreOfferCandidateReducer.changeStatusMeta
  );
  const offerStatusMeta = useSelector(
    (state) => state.PreOfferCandidateReducer.offerStatusMeta
  );
  const jobTagMeta = useSelector((state) => state.JobStageReducer.jobTagMeta);

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const handleClose = () => {
    setElcFormOpen(false);
  };

  const handleOpenModal = (formName) => {
    setElcModalOpen(true);
    setModalForm(formName);
  };

  const handleVerify = () => {
    dispatch(changeStatus({ id: selectedCandidate?.id, status: "VERIFIED" }));
  };

  // Hardcoded for retrieving from specific TOS
  useEffect(() => {
    if (selectedCandidate) {
      if (selectedCandidate?.status === "OFFER_APPROVED") {
        setOffcanvasWidth("100vw");
      } else {
        setOffcanvasWidth("70vw");
      }

      getTOSByJobIdAndCandidateIdAndStatus(
        selectedCandidate?.jobEnity?.id,
        selectedCandidate?.candidate?.id,
        "SUBMITTED_OR_PREPARE"
      ).then((response) => setTosData(response?.data?.tosSubmissionData));
    }
  }, [selectedCandidate]);

  // Toggle Offcanvas Width
  const toggleOffcanvasWidth = () => {
    setOffcanvasWidth(offcanvasWidth === "100vw" ? "70vw" : "100vw");
  };

  const getHeading = (status) => {
    if (showTOS) {
      return "TOS";
    }
    switch (status) {
      case "COMPLETED":
        return "Verify Pre-Offer";
      case "VERIFIED":
      case "CREATE_OFFER_DRAFT":
        return "Create Offer";
      case "OFFER_RENEGOTIATION":
        return "Recreate Offer";
      case "REJECTED":
        return "Reverify Pre-Offer";
      case "CREATE_OFFER_SUBMITTED":
        return "Review Offer";
      case "OFFER_REVIEWED":
        return "Approve Offer";
      case "CORRECTION_NEEDED":
        return "Edit Offer";
      case "OFFER_APPROVED":
        return "Send Offer";
      case "ONBOARDING_PENDING":
        return "Upload Onboarding Documents";
      case "ONBOARDING_DOCUMENTS_UPLOADED":
        return "Onboarding";
      default:
        return "Verify Pre-Offer";
    }
  };

  const offCanvasBody = (status) => {
    if (showTOS) {
      return (
        <EditTOS
          offerData={selectedCandidate}
          ref={formikRef}
          tosData={tosData}
          handleClose={handleClose}
        />
      );
    }
    switch (status) {
      case "COMPLETED":
      case "REJECTED":
        return (
          <PreOfferVerification
            selectedCandidate={selectedCandidate}
            toggleTab={toggleTab}
            activeTab={activeTab}
            tosData={tosData}
          />
        );
      case "VERIFIED":
      case "OFFER_RENEGOTIATION":
        return (
          <OfferCreation
            offerData={selectedCandidate}
            ref={formikRef}
            setOfferUploaded={setOfferUploaded}
            setLoading={setLoading}
          />
        );
      case "CREATE_OFFER_DRAFT":
      case "CORRECTION_NEEDED":
        return (
          <OfferCreation
            offerData={selectedCandidate}
            ref={formikRef}
            edit={true}
            correction={true}
            setOfferUploaded={setOfferUploaded}
            setLoading={setLoading}
          />
        );
      case "CREATE_OFFER_SUBMITTED":
      case "OFFER_REVIEWED":
        return (
          <OfferCreation
            offerData={selectedCandidate}
            ref={formikRef}
            edit={true}
            setOfferUploaded={setOfferUploaded}
            setLoading={setLoading}
          />
        );
      case "OFFER_APPROVED":
        return (
          <ELCShareOffer
            ref={formikRef}
            offerData={selectedCandidate}
            setOffcanvasWidth={setOffcanvasWidth}
            setIsViewTemplate={setIsViewTemplate}
            setTemplatePreviewInfo={setTemplatePreviewInfo}
            setTemplatePreviewAction={setTemplatePreviewAction}
          />
        );
      case "ONBOARDING_PENDING":
        return (
          <PreOfferVerification
            selectedCandidate={selectedCandidate}
            toggleTab={toggleTab}
            activeTab={activeTab}
            tosData={tosData}
            onboarding={true}
          />
        );
      default:
        return "Verify Pre-Offer";
    }
  };

  const renderActionButtons = (status) => {
    if (showTOS) {
      return (
        <>
          <Button className="elc-cancel-btn" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            className="elc-submit-btn"
            onClick={() => formikRef.current.submitForm()}
            disabled={jobTagMeta?.isLoading || loading}
          >
            {jobTagMeta?.isLoading || loading ? (
              <Spinner size="sm" color="light" />
            ) : (
              <>Edit</>
            )}
          </Button>
        </>
      );
    }
    switch (status) {
      case "COMPLETED":
      case "REJECTED":
        return (
          <>
            <Button
              className="elc-cancel-btn"
              onClick={handleClose}
              disabled={changeStatusMeta?.isLoading}
            >
              Cancel
            </Button>
            <Button
              className="elc-submit-btn"
              onClick={handleVerify}
              disabled={changeStatusMeta?.isLoading}
            >
              {changeStatusMeta?.isLoading ? (
                <Spinner size="sm" color="light" />
              ) : status === "REJECTED" ? (
                "Reverify"
              ) : (
                "Verify"
              )}
            </Button>
            <Button
              className="elc-reject-btn"
              onClick={() => handleOpenModal("reject_preoffer")}
              disabled={changeStatusMeta?.isLoading}
            >
              Reject
            </Button>
          </>
        );
      case "VERIFIED":
      case "CREATE_OFFER_DRAFT":
      case "OFFER_RENEGOTIATION":
        return (
          <>
            {isOfferUploaded && (
              <Button
                className="elc-reject-btn"
                onClick={() => formikRef.current.deleteFile(0)}
              >
                Choose other file
              </Button>
            )}
            <Button className="elc-cancel-btn" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              className="elc-submit-btn"
              onClick={() => formikRef.current.submitForm("submit")}
              disabled={offerStatusMeta?.isLoading || loading}
            >
              {offerStatusMeta?.isLoading || loading ? (
                <Spinner size="sm" color="light" />
              ) : (
                <>{status === "OFFER_RENEGOTIATION" ? "Recreate" : "Create"}</>
              )}
            </Button>
          </>
        );
      case "CREATE_OFFER_SUBMITTED":
        return (
          <>
            <Button className="elc-cancel-btn" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              className="elc-submit-btn"
              onClick={() => handleOpenModal("review_offer")}
              disabled={offerStatusMeta?.isLoading}
            >
              {offerStatusMeta?.isLoading ? (
                <Spinner size="sm" color="light" />
              ) : (
                "Review"
              )}
            </Button>
          </>
        );
      case "OFFER_REVIEWED":
        return (
          <>
            <Button className="elc-cancel-btn" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              className="elc-submit-btn"
              onClick={() => handleOpenModal("approve_offer")}
              disabled={offerStatusMeta?.isLoading}
            >
              {offerStatusMeta?.isLoading ? (
                <Spinner size="sm" color="light" />
              ) : (
                "Approve"
              )}
            </Button>
          </>
        );
      case "CORRECTION_NEEDED":
        return (
          <>
            {isOfferUploaded && (
              <Button
                className="elc-reject-btn"
                onClick={() => formikRef.current.deleteFile(0)}
              >
                Choose other file
              </Button>
            )}
            <Button className="elc-cancel-btn" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              className="elc-submit-btn"
              onClick={() => formikRef.current.submitForm("submit")}
              disabled={offerStatusMeta?.isLoading}
            >
              {offerStatusMeta?.isLoading ? (
                <Spinner size="sm" color="light" />
              ) : (
                "Edit"
              )}
            </Button>
          </>
        );
      case "OFFER_APPROVED":
        return (
          <div className="d-flex justify-content-end gap-2">
            <Button
              style={{
                backgroundColor: "#FFFFFF",
                border: "1px solid #E7EAEE",
                color: "#000000",
                fontWeight: "500",
                borderRadius: "8px",
              }}
              type="button"
              className="d-flex align-items-center justify-content-center"
              onClick={toggleOffcanvasWidth}
              disabled={offerStatusMeta?.isLoading}
            >
              <span className="mdi mdi-dock-right"></span>
            </Button>
            <Button
              className="btn btn-white bg-gradient border-2 border-light-grey fw-semibold"
              style={{
                borderRadius: "8px",
              }}
              onClick={handleClose}
              disabled={offerStatusMeta?.isLoading}
            >
              Cancel
            </Button>
            <Button
              className="btn btn-success"
              onClick={() => {
                formikRef.current.submitForm();
              }}
              style={{
                borderRadius: "8px",
              }}
              disabled={offerStatusMeta?.isLoading}
            >
              {offerStatusMeta?.isLoading ? (
                <Spinner size="sm" color="light" />
              ) : (
                <span>Send</span>
              )}
            </Button>
          </div>
        );
      case "ONBOARDING_PENDING":
        return (
          <div className="d-flex justify-content-end gap-2">
            <Button
              className="elc-cancel-btn"
              onClick={handleClose}
              disabled={changeStatusMeta?.isLoading}
            >
              Cancel
            </Button>
            <Button
              className="btn btn-success"
              onClick={() => {
                handleOpenModal("onboarding_upload_done");
              }}
              style={{
                borderRadius: "8px",
              }}
              disabled={changeStatusMeta?.isLoading}
            >
              {changeStatusMeta?.isLoading ? (
                <Spinner size="sm" color="light" />
              ) : (
                <span>Done</span>
              )}
            </Button>
          </div>
        );
      default:
        return;
    }
  };

  return (
    <Fragment>
      <Offcanvas
        isOpen={isOpen}
        toggle={handleClose}
        direction="end"
        style={{ width: offcanvasWidth }}
      >
        <OffcanvasBody className="p-0">
          <div className="offcanvas-header d-flex flex-row justify-content-between align-items-center py-3">
            <div className="d-flex flex-column gap-2">
              <div className="d-flex flex-row align-items-center gap-2">
                <div className="elc-forms-title-tag">
                  <span>{getHeading(selectedCandidate?.status)}</span>
                </div>
                <span className="fw-semibold fs-5">
                  {selectedCandidate?.candidate?.firstName}{" "}
                  {selectedCandidate?.candidate?.lastName}
                </span>
              </div>
              <div>
                <span className="elc-subtitle">
                  {selectedCandidate?.jobEnity?.jobSubmissionData?.jobTitle} -{" "}
                  {selectedCandidate?.jobEnity?.jobSubmissionData?.jobId} -
                  {selectedCandidate?.jobEnity?.jobSubmissionData?.workLocation}
                </span>
              </div>
            </div>
            <div className="d-flex flex-row gap-2">
              {renderActionButtons(selectedCandidate?.status)}
            </div>
          </div>
          {offCanvasBody(selectedCandidate?.status)}
        </OffcanvasBody>

        {/* View Template */}
        <TemplatePreviewSideDrawer
          showSideDrawer={isViewTemplate}
          setShowSideDrawer={setIsViewTemplate}
          templatePreviewInfo={templatePreviewInfo}
          templatePreviewAction={templatePreviewAction}
          jobTimeLineData={{
            candidate: { id: selectedCandidate?.candidate?.id },
            job: { id: selectedCandidate?.jobEnity?.id },
          }}
          fetchSingleTemplate={{
            category: "Email Templates",
            template: "Associate",
          }}
          formValues={{}}
          readOnly={true}
        />
      </Offcanvas>
    </Fragment>
  );
}

export default ELCForms;
